<template>
  <ModalDialog
    v-model="localIsOpened"
    is-wide
    dialog-class="office-selector-dialog"
    data-qa="office-selector-dialog"
    @hidden="$emit('hidden')"
  >
    <template #header>
      <default-header :title="$res.Views.OfficeSelectorDialog_Title"></default-header>
    </template>

    <template #content>
      <div class="osd">
        <div class="location-filter">
          <form autocomplete="off" @submit.prevent>
            <InputControl v-model="locationFilterText" :placeholder="$res.Views.OfficeSelectorDialog_FindACity">
              <template #end>
                <button tabindex="-1" type="button" class="input-button" v-show="!!locationFilterText" @click="locationFilterText = ''">
                  <svg-icon icon="lh-close" class="text-main-10"></svg-icon>
                </button>
                <button tabindex="-1" type="button" class="input-button" v-show="!locationFilterText">
                  <svg-icon v-show="!locationFilterText" icon="lh:search" class="text-main-10"></svg-icon>
                </button>
              </template>
            </InputControl>
          </form>
        </div>
        <ul class="osd__chips chip-list">
          <li v-for="location of topOfficeLocations" :key="location.Id" class="chip-list__chip"
              @click="onTopLocationClick(location.Id)">{{ location.Name }}
          </li>
        </ul>
        <div class="osd__layout" v-if="regions.length > 0 || locations.length > 0">
          <office-location-list class="osd__city-select"
                                v-model="selectedRegion"
                                :title="$res.General.Region"
                                key-id="Id"
                                key-text="Name"
                                :items="regions"
                                @update:value="onSelectedRegionChange"
                                :group-title-by-id-fn="id => referenceDescription.Countries.find(c => c.Id === id).Name"
                                key-group-id="CountryId"
                                :render-group-title-fn="countryId => countryId !== options.mainCountryId"
                                :sort-items-in-group-fn="sortRegionsByName"
                                :sort-groups-fn="sortCountriesByName"
          ></office-location-list>
          <office-location-list class="osd__city-select"
                                v-model="selectedLocation"
                                :title="$res.General.Location"
                                key-id="Id"
                                key-text="Name"
                                :items="locations"
                                :sort-items-in-group-fn="sortLocationsByName"
                                :highlight-substring="highlightSubstring"
                                @update:value="onSelectedLocationChange"
          ></office-location-list>
        </div>
        <div v-else class="osd__no-results no-results">
          <SvgIcon icon="lh:pic-search" width="100" height="100"/>
          <div class="no-results__text">
            {{ $res.General.NoMatchingResults }}
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <default-footer
        :action-btn-disabled="!selectedLocation"
        :close-btn-text="$res.General.Cancel"
        :action-btn-text="$res.General.ToSave"
        @action-clicked="onSave"
      >
      </default-footer>
    </template>
  </ModalDialog>
</template>

<script>
import SvgIcon from "@/common/controls/SvgIconControl.vue";
import ModalDialog from "@/common/modal/DataDialogComponentDesktopBase.vue";
import DefaultHeader from "@/common/modal/DataDialogDefaultHeaderComponentDesktop.vue";
import DefaultFooter from "@/common/modal/DataDialogDefaultFooterComponentDesktop.vue";
import InputControl from "@/common/controls/forms/input/InputControl.vue";
import OfficeLocationList from "./OfficeLocationListComponentDesktop.vue";

export default {
  components: {
    SvgIcon,
    ModalDialog,
    DefaultHeader,
    DefaultFooter,
    OfficeLocationList,
    InputControl,
  },
  emits: ["save", "update:modelValue", "hidden"],
  props: {
    currentLocation: Object,
    options: Object,
    referenceDescription: Object,
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedRegion: null,
      selectedLocation: null,
      allRegions: [],
      topOfficeLocations: [],
      locationFilterText: "",
    };
  },
  computed: {
    localIsOpened: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    highlightSubstring() {
      if (this.locationFilterText && this.locationFilterText.length >= 1) {
        return this.locationFilterText;
      }
      return null;
    },
    regions() {
      if (!this.highlightSubstring && !this.selectedRegion) {
        return this.allRegions;
      }
      let locations = this.options.locationIds.map(id => this.referenceDescription.Locations.find(x => x.Id === id));

      if (this.highlightSubstring) {
        const text = this.highlightSubstring.toLowerCase();
        locations = locations.filter(location => location.Name.toLowerCase().includes(text));
      }

      return this.allRegions.filter(region => locations.some(location => location.RegionId === region.Id));
    },
    locations() {
      if (!this.highlightSubstring && !this.selectedRegion) {
        return [];
      }

      let locations = this.options.locationIds.map(id => this.referenceDescription.Locations.find(x => x.Id === id));

      // если по задан текстовый фильтр, то фильтр пожходящих по всем регионам, иначе показываем выбранный
      if (this.highlightSubstring) {
        const text = this.highlightSubstring.toLowerCase();
        locations = locations.filter(location => location.Name.toLowerCase().includes(text));
      } else if (this.selectedRegion) {
        locations = locations.filter(location => location.RegionId === this.selectedRegion);
      }

      locations.sort((a, b) => a.Name.localeCompare(b.Name));
      return locations;
    },
  },
  mounted() {
    this.allRegions = this.createRegionsModelFromOptions();
    this.topOfficeLocations = this.options.topLocationIds.map(id => this.referenceDescription.Locations.find(x => x.Id === id));
    this.selectedRegion = this.currentLocation.regionId;
    this.selectedLocation = this.currentLocation.id;
  },
  methods: {
    createRegionsModelFromOptions() {
      const regions = this.options.regionIds.map(id => this.referenceDescription.Regions.find(x => x.Id === id));
      // regions.sort((a, b) => a.Name.localeCompare(b.Name));
      return regions;
    },

    onSave() {
      if (!this.selectedLocation) {
        return;
      }

      this.$emit("save", this.selectedLocation);
    },

    onTopLocationClick(id) {
      this.selectedLocation = id;
      this.onSave();
    },

    onSelectedRegionChange() {
      this.selectedLocation = null;
    },

    onSelectedLocationChange() {
      if (this.selectedLocation) {
        const location = this.locations.find(x => x.Id === this.selectedLocation);
        this.selectedRegion = location.RegionId;
      } else {
        this.selectedRegion = null;
      }
    },
    
    sortCountriesByName(idA, idB) {
      // mainCountryId first
      if(idA === this.options.mainCountryId && idB !== this.options.mainCountryId) {
        return -1;
      }

      // mainCountryId first
      if(idA !== this.options.mainCountryId && idB === this.options.mainCountryId) {
        return 1;
      }

      const countryA = this.referenceDescription.Countries.find(c => c.Id === idA);
      const countryB = this.referenceDescription.Countries.find(c => c.Id === idB);

      return countryA.Name.localeCompare(countryB.Name);
    },
    
    sortRegionsByName(regionA, regionB) {
      return regionA.Name.localeCompare(regionB.Name);
    },
    
    sortLocationsByName(locationA, locationB) {
      return locationA.Name.localeCompare(locationB.Name);
    },    
    
  },
};
</script>

<style lang="scss" scoped>
:deep(.office-selector-dialog) {
  width: 784px !important;
  
  .modal-content {
    width: 784px !important;    
  }
}

.osd__chips {
  margin-top: 15px
}

.chip-list {
  list-style-type: none;
  margin-bottom: 0;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  font-size: 0;
}

.chip-list__chip {
  padding: 5px 8px;
  border-radius: 1000px;
  background-color: var(--main-40);
  display: inline-block;
  cursor: pointer;
  font-size: 13px;
  line-height: 20px;
  
  [theme="hb"] & {
    background-color: var(--status-available-30);
    color: var(--second-10);
  }

  + .chip-list__chip {
    margin-inline-start: 10px;
  }
}

.osd__layout {
  margin-top: 15px;
  display: flex;
  flex-flow: row nowrap;
  gap: 24px;

  > * {
    flex: 1 1 50%;
    min-width: 1px;
  }
}

.osd__region-select,
.osd__city-select {
  height: 304px;
}

.location-filter {
  position: relative;

  button {
    position: absolute;
    inset-block-start: 3px;
    inset-inline-end: 8px;
    padding: 2px;
    border: none;
    background-color: transparent;
  }

  input::placeholder {
    color: var(--second-20);
  }
}

.osd__no-results {
  padding: 87px 0;
}

.no-results {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.no-results__text {
  font-size: 16px;
  line-height: 24px;
  text-align: center;
}

.input {
  width: 100%;
}

.input-button {
  appearance: none;
  border: none;
  display: flex;
  width: 20px;
  height: 20px;
  outline: none!important;
  align-items: center;
  justify-content: center;
  background: transparent;
}
</style>
